import {
  Avatar,
  Breadcrumb,
  BreadcrumbProps,
  Button,
  CompactSidebarNavTrigger,
  Heading,
  Icon,
  NavLink,
  Page,
  SidebarNav,
  SidebarNavContent,
  SidebarNavFooter,
  SidebarNavHeader,
  SidebarNavMenu,
  SidebarNavMenuButton,
  SidebarNavMenuItem,
  SidebarNavProvider,
  Tooltip,
  TooltipProvider,
  useIsMobile,
  useSidebarNav
} from '@aidkitorg/component-library';
import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';
import { Text } from '@aidkitorg/types/lib/survey';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext } from '../Context';
import { useTheme } from '../Contexts/ThemeContext';
import { useLocalizedStrings } from '../Localization';
import { LanguageDropdown } from './common/LanguageDropdown';

export type AdminPanelPageProps = React.PropsWithChildren<{
  title: React.ReactNode | string,
  description?: React.ReactNode,
  breadcrumbs?: BreadcrumbProps['items'],
  pageActionButtons?: React.ReactElement<React.ComponentProps<typeof Button>>[],
}>

const AdminPanelSidebarHeader = () => {
  const { state, isMobile, expandedTriggerRef } = useSidebarNav();
  const publicConfig = useContext(PublicConfigurationContext);
  const { logo } = useTheme();
  const L = useLocalizedStrings();
  const [showLogo, setShowLogo] = useState(state === 'expanded');
  const interfaceContext = useContext(InterfaceContext);
  const isRTL = interfaceContext.direction === 'rtl';
  const history = useHistory();

  // This is a subtle animation polish to ensure that the logo 
  // gradually appears in tandem with the sidebar nav expansion transition
  useEffect(() => {
    if (state === 'expanded') {
      // Wait for the next frame before showing the logo
      const frame = requestAnimationFrame(() => {
        setShowLogo(true);
      });
      
      return () => cancelAnimationFrame(frame);
    } else {
      // Hide the logo immediately when collapsing
      setShowLogo(false);
    }
  }, [state]);

  if (isMobile) {
    // Sheets will render a floating X close button in the top right corner
    // so the right margin is set to 9rem to account for that
    return <div className="px-[0.5rem] pb-2 mr-9 rtl:ml-9">
      <span className="text-gray-700 font-semibold text-sm">
        {publicConfig?.name || "AidKit"}
      </span>
    </div>
  }

  return (
    <div className={`flex justify-between items-start ${isMobile ? "mt-5" : ''}`}>
      <div className="w-auto rtl:sm:pl-4 sm:pr-4 max-w-48 flex-shrink-0">
        <div 
          className={`duration-200 transition-opacity ease-out ${showLogo ? 'opacity-100' : 'opacity-0'} hover:cursor-pointer focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 rounded-sm`}
          onClick={() => {
            history.push("/admin-panel/overview")
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              history.push("/admin-panel/overview");
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={L.admin_panel.nav.dashboard_home}
        >
          {logo}
        </div>
      </div>
      <div className={state !== 'collapsed' ? 'block' : 'hidden'} >
        <Tooltip 
          text={L.component_library.sidebar_nav.collapse_sidebar} 
          side={isRTL ? "left" : "right"} 
          asChild
        >
          <div>
            <CompactSidebarNavTrigger ref={expandedTriggerRef} />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

const AdminPanelNav = () => {
  const interfaceContext = useContext(InterfaceContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const config = useContext(ConfigurationContext);
  const { path } = useRouteMatch();
  const history = useHistory();
  const L = useLocalizedStrings();
  const isRTL = interfaceContext.direction === 'rtl';
  const { state, isMobile, open } = useSidebarNav();
  const [showAvatar, setShowAvatar] = useState<boolean>(state === 'expanded');

  // Delay the avatar from appearing until the sidebar width transition is complete
  useEffect(() => {
    if (state === 'expanded') {
      const timer = setTimeout(() => {
        setShowAvatar(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Hide avatar immediately when collapsing
      setShowAvatar(false);
    }
  }, [state]);

  // TODO: update Theme provider to take in a size for the logo
  const NAV_ITEMS: Array<NavigationNode & { iconName: IconName }> = [
    {
      name: L.admin_panel.nav.dashboard_home,
      href: '/admin-panel/overview',
      iconName: 'HomeIcon'
    },
    {
      name: L.admin_panel.nav.payments,
      href: '/admin-panel/payments',
      iconName: 'CurrencyDollarIcon'
    },
    {
      name: L.admin_panel.nav.accounts,
      href: '/admin-panel/accounts',
      iconName: 'WalletIcon'
    },
    // {
    //   name: L.admin_panel.nav.dashboards,
    //   href: '/admin-panel/my-dashboards',
    //   iconName: 'PresentationChartBarIcon'
    // }
  ]

  const getTranslatedText = (lang?: keyof Text, value?: string | Text) => {
    if (typeof value === 'string' || !value) {
      return value;
    }
    return value[lang ?? 'en'] ?? value['en'];
  }

  return (
    <TooltipProvider>
      <SidebarNav
        title={publicConfig?.name || "AidKit"}
        collapsible='icon'
        className="bg-white"
      >
        <SidebarNavHeader>
          <AdminPanelSidebarHeader />
        </SidebarNavHeader>

        <SidebarNavContent>
          <div className="h-full flex flex-col justify-between">
            <SidebarNavMenu>
              {NAV_ITEMS.map((item, idx) => {
                const menuButton = (
                  <SidebarNavMenuButton 
                    href={item.href} 
                    onClick={(e) => {
                      history.push(item.href)
                      e.preventDefault()
                    }}
                    isActive={path.includes(item.href)}
                  >
                    <Icon name={item.iconName} type="solid" role="decorative" />
                    <span>{getTranslatedText(interfaceContext.lang, item.name)}</span>
                  </SidebarNavMenuButton>
                );

                return (
                  <SidebarNavMenuItem key={idx}>
                    {state === 'collapsed' && !isMobile ? (
                      <Tooltip 
                        text={getTranslatedText(interfaceContext.lang, item.name)} 
                        side={isRTL ? "left" : "right"} 
                        skipProvider={true}
                        asChild
                      >
                        {menuButton}
                      </Tooltip>
                    ) : (
                      menuButton
                    )}
                  </SidebarNavMenuItem>
                )
              })}
            </SidebarNavMenu>
            {!isMobile && !open && (
              <Avatar layout="condensed" size="sm" name={`${config.user.name}`} />
            )}
          </div>
        </SidebarNavContent>

        <SidebarNavFooter>
          {config?.user && (isMobile || state === 'expanded') && (
            <Avatar
              name={`${config.user.name}`} 
              email={config.user.email}
              link={
                publicConfig.adminPasswords && (
                  <NavLink to="/admin/account_settings">{L.admin.account_settings.title}</NavLink>
                )
              }
              className={`my-2 transition-opacity duration-100 ease-out ${showAvatar ? 'opacity-100' : 'opacity-0'}`}
            />
          )}
          <Button variant="secondary" onClick={(e) => { window.location.assign("/logout") }}>
            {L.menu.logout}
          </Button>
        </SidebarNavFooter>
      </SidebarNav>
    </TooltipProvider>
  )
};

export const AdminPanelPage: React.FC<AdminPanelPageProps> = ({
  title,
  description,
  breadcrumbs,
  pageActionButtons,
  children
}) => {
  const L = useLocalizedStrings();
  const { languages } = useTheme();
  const interfaceContext = useContext(InterfaceContext);
  const isRTL = interfaceContext.direction === 'rtl';

  // TODO: uncomment this before releasing
  // if (publicConfig && publicConfig.adminPanel === false){
  //   history.push("/");
  // }

  const PageHeader = () => {
    const { mobileTriggerRef } = useSidebarNav();
    const isMobile = useIsMobile();
    
    return (
      <>
        <div className="w-full border-b bg-white">
          <div className="flex flex-col gap-y-1 py-[0.5rem] sm:gap-y-3 sm:py-[1rem]">
            <div className="w-full max-w-screen-2xl mx-auto px-[0.5rem] sm:px-[1rem]">
              {isMobile && (
                <div className="flex flex-col gap-y-2">
                  <div className="flex flex-row gap-x-2 pb-2">
                    <Tooltip
                      text={L.component_library.sidebar_nav.expand_sidebar}
                      side={isRTL ? "left" : "right"}
                      asChild
                    >
                      <div>
                        <CompactSidebarNavTrigger ref={mobileTriggerRef} />
                      </div>
                    </Tooltip>
                    {breadcrumbs && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <Breadcrumb items={breadcrumbs} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isMobile && (
                <div className="hidden sm:flex sm:items-center gap-x-2">
                  <div className="flex flex-row gap-x-1.5">
                    {breadcrumbs && (
                      <div className="flex gap-2 items-center">
                        <Breadcrumb items={breadcrumbs} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-end max-w-screen-2xl mx-auto px-[0.5rem] sm:px-[1rem] gap-2">
              <div className="flex flex-col gap-y-1">
                <Heading level="h1" className="m-0">{title}</Heading>
                {description && (
                  <div className="font-medium text-gray-600">
                    {description}
                  </div>
                )}
              </div>
              {pageActionButtons && (
                <div className="flex flex-row gap-2 flex-shrink-0">
                  {pageActionButtons.map((button, idx) => <React.Fragment key={idx}>{button}</React.Fragment>)}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  const footer = (
    <div className="w-full flex flex-col gap-y-2 sm:flex-row sm:justify-between sm:gap-x-2 sm:items-center">
      <div className="w-full flex flex-row space-x-5">
        <div>
          <Button variant="link" href="/privacy.html" openInNewWindow>
            {L.privacy_policy}
          </Button>
        </div>
        <div>
          <Button variant="link" href="/tos.html" openInNewWindow>
            {L.terms_and_conditions}
          </Button>
        </div>
      </div>
      <LanguageDropdown languages={languages} />
    </div>
  )

  return (
    <SidebarNavProvider>
      <Page
        className="bg-gray-50 min-w-72"
        header={<PageHeader />}
        sidebar={
          <AdminPanelNav />
        }
        footer={footer}
      >
        {children}
      </Page>
    </SidebarNavProvider>
  )
}